import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  Chip,
  ThemeProvider,
  createTheme,
  Avatar,
  Divider,
  useMediaQuery,
  IconButton
} from '@mui/material';
import {
  MenuBook as ReadIcon,
  Timeline as ProgressIcon,
  Translate as LanguageIcon,
  School as MasteryIcon,
  Mic as SpeakIcon,
  Chat as ChatIcon,
  Lightbulb as InsightIcon,
  ArrowForward as ArrowIcon,
  AutoAwesome as SparklesIcon,
  Refresh as RefreshIcon,
  BarChart as ChartIcon,
  Timelapse as SRSIcon,
  EmojiEvents as TrophyIcon,
  LocalFireDepartment as FireIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Enhanced theme with a cleaner color palette
const theme = createTheme({
  palette: {
    primary: {
      main: '#3a7bd5',
      light: '#6fa6ff',
      dark: '#0054a3',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#ff5f72',
      light: '#ff91a0',
      dark: '#c62a47',
      contrastText: '#ffffff'
    },
    background: {
      default: '#f8faff',
      paper: '#ffffff'
    },
    text: {
      primary: '#2d3748',
      secondary: '#4a5568'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.025em'
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.025em'
    },
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.01em'
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em'
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    }
  },
  shape: {
    borderRadius: 12
  },
  shadows: [
    'none',
    '0px 1px 3px rgba(0,0,0,0.05), 0px 1px 2px rgba(0,0,0,0.07)',
    '0px 3px 6px rgba(0,0,0,0.08), 0px 2px 4px rgba(0,0,0,0.06)',
    '0px 10px 20px rgba(0,0,0,0.09), 0px 3px 6px rgba(0,0,0,0.07)',
    '0px 15px 25px rgba(0,0,0,0.1), 0px 5px 10px rgba(0,0,0,0.08)',
    '0px 20px 40px rgba(0,0,0,0.12)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '10px 24px',
          boxShadow: '0px 4px 14px rgba(58, 123, 213, 0.2)',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 6px 20px rgba(58, 123, 213, 0.25)',
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          overflow: 'hidden',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0px 12px 20px rgba(0, 0, 0, 0.1)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
        }
      }
    }
  }
});

const VisitingPage = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [animateItems, setAnimateItems] = useState(false);

  useEffect(() => {
    // Trigger animations after component mounts
    setTimeout(() => {
      setAnimateItems(true);
    }, 300);
  }, []);

  const handleGetStarted = () => {
    navigate('/signup');
  };

  const supportedLanguages = [
    { code: 'spanish', name: 'Spanish', flag: 'https://flagcdn.com/w40/es.png' },
    { code: 'german', name: 'German', flag: 'https://flagcdn.com/w40/de.png' },
    { code: 'french', name: 'French', flag: 'https://flagcdn.com/w40/fr.png' }
  ];

  const features = [
    {
      icon: <SRSIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: 'Smart Review System',
      description: 'Reviews are scheduled at optimal intervals to help you remember what you learn'
    },
    {
      icon: <SpeakIcon fontSize="large" sx={{ color: theme.palette.secondary.main }} />,
      title: 'Active Recall Practice',
      description: 'Practice through speaking, typing, and interactive exercises'
    },
    {
      icon: <InsightIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: 'Adaptive Learning',
      description: 'Learning adapts based on your performance to help you improve faster'
    },
    {
      icon: <FireIcon fontSize="large" sx={{ color: theme.palette.secondary.main }} />,
      title: 'Progress Tracking',
      description: 'Streaks, XP & progress metrics that show your how far you\'ve come'
    }
  ];

  const learningSteps = [
    {
      number: 1,
      title: 'Course',
      color: theme.palette.primary.light,
      icon: <ReadIcon />,
      description: 'Select your language pathway'
    },
    {
      number: 2,
      title: 'Section',
      color: theme.palette.secondary.main,
      icon: <ProgressIcon />,
      description: 'Focus on themed content'
    },
    {
      number: 3,
      title: 'Courselet',
      color: theme.palette.primary.main,
      icon: <MasteryIcon />,
      description: 'Master specific concepts'
    },
    {
      number: 4,
      title: 'Sentence',
      color: theme.palette.secondary.light,
      icon: <SpeakIcon />,
      description: 'Learn complete, practical phrases'
    }
  ];

  const learningMethods = [
    {
      title: 'Translation Practice',
      icon: <LanguageIcon sx={{ color: theme.palette.primary.main }} />,
      description: 'Type translations between your target language and English'
    },
    {
      title: 'Sentence Building',
      icon: <ReadIcon sx={{ color: theme.palette.secondary.main }} />,
      description: 'Arrange tokens to build complete sentences'
    },
    {
      title: 'Speech Practice',
      icon: <SpeakIcon sx={{ color: theme.palette.primary.main }} />,
      description: 'Repeat after audio prompts with speech recognition feedback'
    }
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100vh',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Subtle background gradient */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '70%',
          background: 'radial-gradient(circle at 70% 30%, rgba(58, 123, 213, 0.05), transparent 60%)',
          zIndex: 0,
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 4, md: 8 } }}>
          {/* Hero Section */}
          <Grid container spacing={4} alignItems="center" sx={{ mb: 12 }}>
            <Grid item xs={12} md={6}
              sx={{
                transform: animateItems ? 'translateY(0)' : 'translateY(20px)',
                opacity: animateItems ? 1 : 0,
                transition: 'all 0.7s ease-out',
              }}
            >
              <Box sx={{ mb: 4 }}>
                <Chip
                  label="Retain Words Better"
                  color="secondary"
                  size="medium"
                  sx={{
                    mb: 2,
                    fontWeight: 'bold',
                    py: 1,
                    px: 1.5
                  }}
                />

                <Typography
                  variant="h2"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.1,
                    mb: 2
                  }}
                >
                  Learn languages{' '}
                  <Box component="span" sx={{
                    color: 'primary.main',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '0.1em',
                      left: 0,
                      width: '100%',
                      height: '0.2em',
                      backgroundColor: 'rgba(58, 123, 213, 0.2)',
                      zIndex: -1
                    }
                  }}> naturally</Box>
                </Typography>

                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 'normal',
                    mb: 4,
                    maxWidth: '90%'
                  }}
                >
                  Our system combines sentence-first learning, interactive practice,
                  and personalized reviews to help you build lasting language skills.
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleGetStarted}
                  endIcon={<ArrowIcon />}
                  sx={{
                    fontSize: '1.125rem',
                    py: 1.5
                  }}
                >
                  Start Learning
                </Button>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  Currently in beta
                </Typography>
              </Box>

              {/* Supported Languages Section */}
              <Box sx={{ mt: 5 }}>
                <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                  Currently supported languages:
                </Typography>

                <Box sx={{ display: 'flex', gap: 3 }}>
                  {supportedLanguages.map((language) => (
                    <Box key={language.code} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <img src={language.flag} alt={`${language.name} flag`} style={{ width: '40px', height: 'auto' }} />
                      <Typography variant="body1" fontWeight="medium">
                        {language.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* App Screenshot/Mock */}
            <Grid item xs={12} md={6}
              sx={{
                transform: animateItems ? 'translateY(0)' : 'translateY(20px)',
                opacity: animateItems ? 1 : 0,
                transition: 'all 0.7s ease-out',
                transitionDelay: '0.2s',
              }}
            >
              <Box
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  overflow: 'hidden',
                  boxShadow: 3,
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <img
                  src="/Card.png"
                  alt="Language learning app interface"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* How Learning Works Section */}
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                textAlign: 'center',
                mb: 2
              }}
            >
              How Our Learning System Works
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                mb: 6,
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              A structured approach designed to build practical language skills
            </Typography>

            <Grid container spacing={5} alignItems="stretch">
              {/* Learning Structure */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    border: '1px solid rgba(58, 123, 213, 0.1)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 4, fontWeight: 'bold', textAlign: 'center' }}>
                    Structured Learning Path
                  </Typography>

                  <Typography variant="body2">
                    Our method teaches language content so that you automatically know when and where you'll apply the concept. No endless vocab lists.
                  </Typography>
                  <br /><br />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, flexGrow: 1 }}>
                    {learningSteps.map((step, index) => (
                      <Box
                        key={step.title}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          transform: animateItems ? 'translateX(0)' : 'translateX(-20px)',
                          opacity: animateItems ? 1 : 0,
                          transition: 'all 0.5s ease-out',
                          transitionDelay: `${0.2 + 0.1 * index}s`,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            backgroundColor: step.color,
                            color: 'white',
                            boxShadow: '0px 4px 10px rgba(0,0,0,0.1)'
                          }}
                        >
                          {step.icon}
                        </Avatar>

                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {step.title}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {step.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              {/* Review System */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    border: '1px solid rgba(58, 123, 213, 0.1)',
                    transform: animateItems ? 'translateY(0)' : 'translateY(30px)',
                    opacity: animateItems ? 1 : 0,
                    transition: 'all 0.6s ease-out',
                    transitionDelay: '0.3s',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <SRSIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      Review System
                    </Typography>
                  </Box>

                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Our review system ensures long-term retention by scheduling reviews at the optimal moment:
                  </Typography>

                  <Box sx={{ mb: 4, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          mr: 2
                        }} />
                        <Typography variant="body2">Flashcards update their level/interval based on your answers</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.secondary.main,
                          mr: 2
                        }} />
                        <Typography variant="body2">Tracks review counts, memory strength, and streaks</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.light,
                          mr: 2
                        }} />
                        <Typography variant="body2">Adapts to your learning pace and performance</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.secondary.light,
                          mr: 2
                        }} />
                        <Typography variant="body2">Combats the natural forgetting curve by reinforcing memory at key moments</Typography>
                      </Box>
                    </Box>

                    <Box sx={{
                      mt: -2,
                      p: 3,
                      borderRadius: 3,
                      backgroundColor: 'rgba(58, 123, 213, 0.05)',
                      border: '1px dashed rgba(58, 123, 213, 0.2)'
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.primary.dark }}>
                        Designed for long-term memory
                      </Typography>
                      <Typography variant="body2">
                        Our review system intelligently schedules practice sessions to move vocabulary from short-term to long-term memory efficiently.
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* Features Section */}
          <br /><br />
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                textAlign: 'center',
                mb: 2
              }}
            >
              Core Features
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                mb: 5,
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Tools designed to make language learning effective and enjoyable
            </Typography>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={3} key={feature.title}
                  sx={{
                    transform: animateItems ? 'translateY(0)' : 'translateY(30px)',
                    opacity: animateItems ? 1 : 0,
                    transition: 'all 0.5s ease-out',
                    transitionDelay: `${0.1 + 0.1 * index}s`,
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      backgroundColor: 'background.paper',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 2
                      }
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      {feature.icon}
                    </Box>

                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          <br /><br />
          {/* Learning Methods Section */}
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                textAlign: 'center',
                mb: 2
              }}
            >
              Interactive Learning Methods
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                mb: 6,
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Multiple exercise types to build strong language skills
            </Typography>

            <Grid container spacing={4}>
              {learningMethods.map((method, index) => (
                <Grid item xs={12} md={4} key={method.title}
                  sx={{
                    transform: animateItems ? 'translateY(0)' : 'translateY(30px)',
                    opacity: animateItems ? 1 : 0,
                    transition: 'all 0.5s ease-out',
                    transitionDelay: `${0.1 + 0.1 * index}s`,
                  }}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 3
                      },
                      border: '1px solid rgba(58, 123, 213, 0.1)',
                    }}
                  >
                    <Box sx={{ mb: 3 }}>
                      {method.icon}
                    </Box>

                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                      {method.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {method.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          <br /><br />
          {/* AI Conversation Section - Moved down */}
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                textAlign: 'center',
                mb: 2
              }}
            >
              AI Conversation Practice
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                mb: 5,
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Practice real-world conversations in a safe environment
            </Typography>

            <Grid container spacing={5} justifyContent="center">
              <Grid item xs={12} md={8}
                sx={{
                  transform: animateItems ? 'translateY(0)' : 'translateY(30px)',
                  opacity: animateItems ? 1 : 0,
                  transition: 'all 0.6s ease-out',
                  transitionDelay: '0.2s',
                }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.05)',
                  }}
                >
                  <Box sx={{ p: 2, background: 'linear-gradient(90deg, #3a7bd5, #6fa6ff)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ChatIcon sx={{ color: 'white' }} />
                      <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold' }}>AI Conversation Practice</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 'medium' }}>Restaurant Scene</Typography>
                  </Box>

                  <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', minHeight: '300px' }}>
                    <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* AI Message */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.light' }}>AI</Avatar>
                        <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'white', borderTopLeftRadius: 0, maxWidth: '80%' }}>
                          <Typography variant="body1">¿Qué le gustaría ordenar hoy?</Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8, fontStyle: 'italic', mt: 1 }}>
                            What would you like to order today?
                          </Typography>
                        </Paper>
                      </Box>

                      {/* User Message */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, alignSelf: 'flex-end' }}>
                        <Paper sx={{ p: 2, bgcolor: 'grey.100', borderTopRightRadius: 0, maxWidth: '80%' }}>
                          <Typography variant="body1">Me gustaría una ensalada y agua, por favor.</Typography>
                        </Paper>
                        <Avatar sx={{ bgcolor: 'grey.400' }}>You</Avatar>
                      </Box>

                      {/* AI Message */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.light' }}>AI</Avatar>
                        <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'white', borderTopLeftRadius: 0, maxWidth: '80%' }}>
                          <Typography variant="body1">Excelente elección. ¿Desea algún aderezo especial para su ensalada?</Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8, fontStyle: 'italic', mt: 1 }}>
                            Excellent choice. Would you like any special dressing for your salad?
                          </Typography>
                        </Paper>
                      </Box>
                    </Box>

                    {/* Non-clickable text input to demonstrate the interface */}
                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.1)',
                        color: 'text.secondary',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Typography variant="body2"></Typography>
                      <Box sx={{ display: 'flex', gap: 1, color: 'primary.main' }}>
                        <SparklesIcon fontSize="small" />
                        <LanguageIcon fontSize="small" />
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* CTA Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              borderRadius: 4,
              background: 'linear-gradient(135deg, #3a7bd5 0%, #6fa6ff 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              transform: animateItems ? 'translateY(0)' : 'translateY(30px)',
              opacity: animateItems ? 1 : 0,
              transition: 'all 0.7s ease-out',
              mb: 4
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  mb: 2,
                  fontWeight: 'bold'
                }}
              >
                Start your language journey
              </Typography>

              <Typography
                variant="h6"
                component="p"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  maxWidth: 700,
                  mx: 'auto',
                  fontWeight: 'normal'
                }}
              >
                Join our beta program and be among the first to experience a better way to learn languages
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                sx={{
                  fontSize: '1.125rem',
                  py: 1.5,
                  px: 4,
                  backgroundColor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              >
                Get Started
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default VisitingPage;